import { forwardRef } from 'react'

/**
 * Presentational primitives.
 *
 * Layer rule, enforced by convention and by review: a file in `src/ui/` contains
 * NO business logic, NO data fetching, NO hardcoded colour / size / font value.
 * Each component emits a stable semantic class name (`ui-btn`, `ui-chip`, …)
 * that the active theme's `skin.css` dresses. Swapping the theme therefore
 * changes appearance without touching a single line here.
 *
 * Where a value must come from data rather than design — a gallery's column
 * count, an avatar's pixel size — it is passed as a CSS custom property on
 * `style`, never as a literal CSS declaration. That keeps the theme in charge
 * of *how* the value is used.
 *
 * 展示层基础组件。
 *
 * 分层规则（靠约定和 review 保证）：src/ui/ 下的文件不含业务逻辑、不含数据获取、
 * 不含任何硬编码的颜色 / 尺寸 / 字体值。每个组件输出稳定的语义类名
 * （ui-btn、ui-chip……），由当前主题的 skin.css 负责外观。因此换主题不需要改动
 * 这里的任何一行。
 *
 * 当某个值必须来自数据而非设计时——图片组的列数、头像的像素尺寸——通过 style 上的
 * CSS 变量传入，绝不写成字面 CSS 声明。这样「值怎么用」的决定权仍在主题手里。
 */

/**
 * Join class names, dropping falsy entries.
 *
 * 拼接类名，忽略假值。
 *
 * @param {...(string|false|null|undefined)} parts Class names / 类名片段。
 * @returns {string} Joined class string / 拼接后的类名串。
 */
export function cx(...parts) {
  return parts.filter(Boolean).join(' ')
}

/**
 * Button.
 *
 * Renders a `<button>`, or an `<a>` when `href` is given, so a link that looks
 * like a button is still a link — right-click, middle-click and screen readers
 * all behave correctly.
 *
 * 按钮。给了 href 时渲染为 <a>，否则渲染为 <button>——这样「长得像按钮的链接」
 * 本质仍是链接，右键、中键和读屏软件的行为都正确。
 *
 * @param {object} props Props / 属性。
 * @param {'primary'|'secondary'|'ghost'|'danger'} [props.variant] Visual role / 视觉角色。
 * @param {'sm'|'md'|'lg'} [props.size] Size step / 尺寸档位。
 * @param {boolean} [props.loading] Show a spinner and block input / 显示加载态并阻止操作。
 * @param {string} [props.href] Render as a link / 渲染为链接。
 * @returns {JSX.Element} Button / 按钮。
 */
export const Button = forwardRef(function Button(
  { variant = 'secondary', size = 'md', loading = false, href, className, children, disabled, ...rest },
  ref
) {
  const classes = cx('ui-btn', `ui-btn--${variant}`, `ui-btn--${size}`, loading && 'is-loading', className)

  if (href) {
    return (
      <a ref={ref} className={classes} href={href} aria-disabled={disabled || undefined} {...rest}>
        {children}
      </a>
    )
  }
  return (
    <button ref={ref} className={classes} disabled={disabled || loading} {...rest}>
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  )
})

/**
 * Icon-only button.
 *
 * `label` is mandatory: an icon button with no accessible name is invisible to
 * a screen reader.
 *
 * 纯图标按钮。label 是必填的：没有可访问名称的图标按钮对读屏软件是隐形的。
 *
 * @param {object} props Props / 属性。
 * @param {string} props.label Accessible name / 可访问名称。
 * @param {'sm'|'md'} [props.size] Size step / 尺寸档位。
 * @returns {JSX.Element} Icon button / 图标按钮。
 */
export const IconButton = forwardRef(function IconButton(
  { label, size = 'md', className, children, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cx('ui-icon-btn', `ui-icon-btn--${size}`, className)}
      aria-label={label}
      title={label}
      {...rest}
    >
      {children}
    </button>
  )
})

/**
 * Chip — a small filter or metadata token.
 *
 * 标签片 —— 小型筛选或元数据标记。
 *
 * @param {object} props Props / 属性。
 * @param {boolean} [props.active] Selected state / 选中状态。
 * @param {() => void} [props.onClick] Makes it a button / 传入即渲染为按钮。
 * @returns {JSX.Element} Chip / 标签片。
 */
export function Chip({ active = false, onClick, className, children, ...rest }) {
  const classes = cx('ui-chip', active && 'is-active', onClick && 'ui-chip--interactive', className)
  if (!onClick) {
    return (
      <span className={classes} {...rest}>
        {children}
      </span>
    )
  }
  return (
    <button type="button" className={classes} onClick={onClick} aria-pressed={active} {...rest}>
      {children}
    </button>
  )
}

/**
 * Badge — a status marker. Semantic tones are for the admin console only; the
 * public site uses the neutral tone.
 *
 * 徽标 —— 状态标记。语义色仅用于后台，公开页只用中性色。
 *
 * @param {object} props Props / 属性。
 * @param {'neutral'|'accent'|'success'|'warning'|'danger'} [props.tone] Tone / 色调。
 * @returns {JSX.Element} Badge / 徽标。
 */
export function Badge({ tone = 'neutral', className, children, ...rest }) {
  return (
    <span className={cx('ui-badge', `ui-badge--${tone}`, className)} {...rest}>
      {children}
    </span>
  )
}

/**
 * Avatar. Falls back to initials when there is no image, so an empty member
 * grid still reads as people rather than broken images.
 *
 * 头像。没有图片时回退为姓名首字，这样成员列表即使缺图也像「人」而不是坏图。
 *
 * @param {object} props Props / 属性。
 * @param {string} [props.src] Image URL / 图片地址。
 * @param {string} props.name Person's name, used for alt text and initials / 姓名，用于 alt 和首字。
 * @param {'sm'|'md'|'lg'} [props.size] Size step / 尺寸档位。
 * @param {'circle'|'square'} [props.shape] Shape / 形状。
 * @returns {JSX.Element} Avatar / 头像。
 */
export function Avatar({ src, name, size = 'md', shape = 'circle', className }) {
  const initials = String(name || '')
    .trim()
    // A Chinese name yields its last two characters; a Latin name yields the
    // initials of the first and last token.
    // 中文名取后两字；拉丁名取首末两词的首字母。
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => (/[⺀-鿿]/.test(part) ? part.slice(-2) : part[0]))
    .slice(0, 2)
    .join('')
    .toUpperCase()

  const classes = cx('ui-avatar', `ui-avatar--${size}`, `ui-avatar--${shape}`, className)

  if (!src) {
    return (
      <span className={cx(classes, 'ui-avatar--fallback')} role="img" aria-label={name}>
        {initials || '·'}
      </span>
    )
  }
  return <img className={classes} src={src} alt={name} loading="lazy" decoding="async" />
}

/**
 * Spinner.
 *
 * 加载指示器。
 *
 * @param {object} props Props / 属性。
 * @param {'sm'|'md'} [props.size] Size step / 尺寸档位。
 * @param {string} [props.label] Accessible label / 可访问标签。
 * @returns {JSX.Element} Spinner / 加载指示器。
 */
export function Spinner({ size = 'md', label, className }) {
  return (
    <span
      className={cx('ui-spinner', `ui-spinner--${size}`, className)}
      role="status"
      aria-label={label || undefined}
      aria-hidden={label ? undefined : 'true'}
    />
  )
}

/**
 * Skeleton placeholder for content that is still loading.
 *
 * Sized in `ch`/`em`-ish terms via a CSS variable rather than pixels, so the
 * theme decides the actual metric.
 *
 * 加载占位块。尺寸通过 CSS 变量以相对单位传入而非像素，实际度量由主题决定。
 *
 * @param {object} props Props / 属性。
 * @param {number} [props.lines] Number of lines / 行数。
 * @param {string} [props.width] CSS width for the last line / 末行宽度。
 * @returns {JSX.Element} Skeleton / 骨架屏。
 */
export function Skeleton({ lines = 1, width = '100%', className }) {
  return (
    <span className={cx('ui-skeleton', className)} aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <span
          key={index}
          className="ui-skeleton__line"
          style={index === lines - 1 ? { '--skeleton-w': width } : undefined}
        />
      ))}
    </span>
  )
}

/**
 * Overline — the small uppercase eyebrow above a heading.
 *
 * 眉标 —— 标题上方的小号全大写标签。
 *
 * @param {object} props Props / 属性。
 * @returns {JSX.Element} Overline / 眉标。
 */
export function Overline({ className, children, ...rest }) {
  return (
    <p className={cx('ui-overline', className)} {...rest}>
      {children}
    </p>
  )
}

/**
 * External link, with `rel` hardened and an affordance glyph.
 *
 * `noopener` is not optional: without it the opened page can reach back through
 * `window.opener` and navigate this tab.
 *
 * 外部链接，rel 已加固并带有外链标记。noopener 不是可选项：没有它，被打开的页面
 * 可以通过 window.opener 反向控制当前标签页的导航。
 *
 * @param {object} props Props / 属性。
 * @param {string} props.href Target URL / 目标地址。
 * @param {boolean} [props.glyph] Show the ↗ affordance / 是否显示 ↗ 标记。
 * @returns {JSX.Element} Anchor / 链接。
 */
export function ExternalLink({ href, glyph = true, className, children, ...rest }) {
  return (
    <a
      className={cx('ui-link', 'ui-link--external', className)}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
    >
      {children}
      {glyph && (
        <span className="ui-link__glyph" aria-hidden="true">
          ↗
        </span>
      )}
    </a>
  )
}

/**
 * Horizontal rule used as a structural hairline.
 *
 * 作为结构分隔用的发丝线。
 *
 * @param {object} props Props / 属性。
 * @returns {JSX.Element} Divider / 分隔线。
 */
export function Hairline({ className }) {
  return <hr className={cx('ui-hairline', className)} />
}
